import type { PurchaseInvoiceDetailRow, PurchaseInvoiceTaxRow } from '../../../types';
import { roundNumber } from '@/lib/formatUtils';

const getTaxIdentity = (row: Partial<PurchaseInvoiceDetailRow | PurchaseInvoiceTaxRow>) =>
  row?.ma_thue_data?.uuid || row?.ma_thue || '';

const getTaxRate = (detailRow: Partial<PurchaseInvoiceDetailRow>, existingTaxRow?: Partial<PurchaseInvoiceTaxRow>) =>
  Number(
    detailRow?.ma_thue_data?.thue_suat ??
      detailRow?.ma_thue_data?.tax_rate ??
      detailRow?.thue_suat ??
      existingTaxRow?.thue_suat ??
      0
  ) || 0;

export const buildTaxRowsFromDetailRows = (
  detailRows: PurchaseInvoiceDetailRow[] = [],
  existingTaxRows: PurchaseInvoiceTaxRow[] = []
) => {
  const existingByIdentity = new Map(existingTaxRows.map(row => [getTaxIdentity(row), row]));
  const grouped: Record<string, PurchaseInvoiceTaxRow> = {};

  detailRows.forEach(row => {
    const taxIdentity = getTaxIdentity(row);
    if (!taxIdentity) return;

    const existingTaxRow = existingByIdentity.get(taxIdentity);
    const taxRate = getTaxRate(row, existingTaxRow);
    const amount = Number(row?.tien_nt0 || 0);
    const taxAmount = row?.thue_nt != null ? Number(row.thue_nt || 0) : roundNumber((amount * taxRate) / 100);

    if (amount <= 0 && taxAmount <= 0 && taxRate <= 0) return;

    if (!grouped[taxIdentity]) {
      grouped[taxIdentity] = {
        ...existingTaxRow,
        uuid: existingTaxRow?.uuid || `tax-${taxIdentity}`,
        line: 0,
        ngay_ct0: existingTaxRow?.ngay_ct0 || new Date().toISOString().split('T')[0],
        ma_thue: row?.ma_thue || existingTaxRow?.ma_thue || row?.ma_thue_data?.uuid || null,
        ma_thue_data: (row?.ma_thue_data ||
          existingTaxRow?.ma_thue_data ||
          null) as PurchaseInvoiceTaxRow['ma_thue_data'],
        thue_suat: taxRate,
        t_tien_nt: 0,
        t_tien: 0,
        t_thue_nt: 0,
        t_thue: 0,
        tk_thue_no_data: (row?.ma_thue_data?.tk_thue_dau_vao_data || existingTaxRow?.tk_thue_no_data || null) as
          | PurchaseInvoiceTaxRow['tk_thue_no_data']
          | null
      };
    }

    grouped[taxIdentity].t_tien_nt = roundNumber(Number(grouped[taxIdentity].t_tien_nt || 0) + amount);
    grouped[taxIdentity].t_tien = grouped[taxIdentity].t_tien_nt;
    grouped[taxIdentity].t_thue_nt = roundNumber(Number(grouped[taxIdentity].t_thue_nt || 0) + taxAmount);
    grouped[taxIdentity].t_thue = grouped[taxIdentity].t_thue_nt;
  });

  return Object.values(grouped).map((row, index) => ({
    ...row,
    line: index + 1
  }));
};

export const getTaxRowIdentity = (row: Partial<PurchaseInvoiceDetailRow | PurchaseInvoiceTaxRow>) =>
  getTaxIdentity(row);
